/**
 * @file Badge atom — status/label chip family.
 * @description structured atom: compound parts re-exported from the vendored
 * Gluestack family; this is the only sanctioned import path for app code
 * (RFC §3). Also exports the narrowed `TonePill` — the deep raw-shade pill
 * pairings (design-to-code.md §2/§4) that app code must never spell out.
 * @module components/atoms/Badge
 */
import React from "react";

import { Box } from "../Box";
import { Text } from "../Text";
import type { TextVariant } from "../types";

export { Badge, BadgeIcon, BadgeText } from "@/components/ui/badge";

/**
 * Deep-shade pill tones. The class positions follow the Figma VARIABLE names
 * bound in the v7 card (GPR badge `success50/success950` — ratified as
 * `rating-positive-surface`/`rating-positive-content-strong` in SE-6522 —
 * Starting XI chip `success0/success900`, watchlist pill `info50/info800`):
 * the raw scales invert across themes (`success-50` = rgb(202,255,232) light /
 * rgb(20,83,45) dark), so the variable position — not the sampled hex — is the
 * theme-safe truth. Raw shades are atom-internal only; this atom is the single
 * place they may appear
 * (SE-5522 pixel audits: GPR badge, StatusChip, RemovablePill).
 */
export type PillTone =
  | "accent"
  | "success"
  | "success-soft"
  | "info-soft"
  | "info-subtle"
  | "info-outline"
  | "info-tinted"
  | "info-status"
  | "neutral-outline"
  | "neutral-raised"
  | "neutral-subtle"
  | "neutral-base"
  | "neutral-base-outline"
  | "neutral-base-outline-strong"
  | "warning-subtle";

/**
 * Tone → class pairing. Exported so structured molecules (icon/pressable
 * compositions the label-only TonePill can't express) consume the SAME
 * pairings without spelling raw shades themselves — the raw class strings
 * exist only here.
 */
/** Secondary-content label class shared by the quiet neutral tones. */
const NEUTRAL_TONE_TEXT = "text-content-secondary";

export const TONE_CLASS: Record<
  PillTone,
  { readonly box: string; readonly text: string }
> = {
  // SE-6554 Review-queue count badge and active type-filter pill: a FILLED
  // accent pill with a white label. Semantic, not a raw shade — `accent-primary`
  // is the CTA fill and `content-on-accent` is the literal white the design
  // pairs with it, so the pill stays legible in both themes without inverting.
  accent: { box: "bg-accent-primary", text: "text-content-on-accent" },
  // SE-6522 rating-annex graduation: the GPR pill pairing renders through the
  // closed `rating.*` annex. Both tokens alias the exact vars the raw
  // `success-50`/`success-950` pairing resolved to, so nothing recoloured —
  // the design's `success50/success950` binding just has ratified names now.
  success: {
    box: "bg-rating-positive-surface",
    text: "text-rating-positive-content-strong",
  },
  "success-soft": { box: "bg-success-0", text: "text-success-900" },
  "info-soft": { box: "bg-info-50", text: "text-info-800" },
  "info-subtle": { box: "bg-info-0", text: "text-info-800" },
  // Bordered info chip (position-filter pill): the only bordered pairing —
  // the border shade rides in `box` so consumers still never spell shades.
  "info-outline": {
    box: "border border-info-300 bg-info-50",
    text: "text-info-600",
  },
  // SE-6554 ACTIVE activity-log filter pill: a TINTED info chip — pale info
  // wash, a one-shade-deeper info border, and the same deep shade as the label.
  // `info-outline` is the neighbouring pairing but not this one: its border is
  // `info-300` and its wash `info-50`, both a step lighter than the design's
  // (236,248,254) fill over an (11,141,205) rule. That deeper rule is `info-600`
  // — NOT the `status-info` anchor, which resolves to `info-500` (13,166,242)
  // and reads visibly brighter. Every shade here inverts with the theme.
  "info-tinted": {
    box: "border border-info-600 bg-info-0",
    text: "text-info-600",
  },
  // SE-8486 (S30): the Add-players card's GPR pill — the STATUS info surface
  // under the info-strong label, the pairing the pop-out row's GPR chip
  // already draws (W73). A semantic pairing, not a raw shade: both halves
  // invert with the theme through the status tier.
  "info-status": {
    box: "bg-status-info-surface",
    text: "text-status-info-strong",
  },
  // V1.6 selected-position chip (SE-5368, Figma 40000926:34404): an unfilled
  // pill whose chrome is semantic, not a raw shade. The design node's border
  // measures (230,230,230) light / (65,65,65) dark and its label a mid
  // neutral — both land exactly on the semantic anchors paired below.
  "neutral-outline": {
    box: "border border-outline-subtle",
    text: NEUTRAL_TONE_TEXT,
  },
  // SE-6554 inactive type-filter pill: the same neutral label, but on a FILLED
  // raised surface behind the one-step-darker default outline. `neutral-outline`
  // is transparent, which disappears against the drawer; the design draws the
  // unselected filter as a visible-but-quiet chip.
  "neutral-raised": {
    box: "border border-outline-default bg-surface-raised",
    text: NEUTRAL_TONE_TEXT,
  },
  // SE-6862 Closed-rail count badge (Figma 80:18285): the neighbouring neutral
  // pairing with its fill stepped DOWN one surface level. The pill sits on a
  // raised lane, and a raised pill on a raised lane is the one place that
  // neighbour vanishes — the design draws this one on the base surface so the
  // count reads as a chip sitting ON the lane rather than as part of it.
  "neutral-base": {
    box: "border border-outline-default bg-surface-base",
    text: NEUTRAL_TONE_TEXT,
  },
  // SE-8016 player-page report row (Perf / Pot / Complete chips): a BORDERLESS
  // quiet fill. The reference binds one flat neutral ground with no rule at
  // all, which none of the neighbouring pairings can express — every other
  // neutral tone here draws a border, and dropping one at the call site would
  // mean spelling chrome outside the atom. Its ground is a step darker than
  // `neutral-raised`'s so the chip still reads on a raised card.
  "neutral-subtle": {
    box: "bg-surface-subtle",
    text: NEUTRAL_TONE_TEXT,
  },
  // SE-6544 mobile map position chip (Figma 40001010:33814): the design binds
  // Background/background0 with a Border/border100 hairline — the base surface
  // under the SUBTLE outline, one step quieter than `neutral-base`'s default
  // rule. `neutral-outline` is the same hairline but transparent, which
  // disappears against the map toolbar's base surface; this one keeps the fill.
  "neutral-base-outline": {
    box: "border border-outline-subtle bg-surface-base",
    text: NEUTRAL_TONE_TEXT,
  },
  // SE-7550 capture position chip (Figma `Row/Pedro Gonçalves` 70:1342):
  // the SAME box as `neutral-base-outline` — the design binds `outline/subtle`
  // over `surface/base` — but the label is `content/primary`, not secondary.
  // A separate pairing rather than a louder text on the existing one, because
  // SE-6544's map chip binds the quiet label deliberately and shares that box.
  "neutral-base-outline-strong": {
    box: "border border-outline-subtle bg-surface-base",
    text: "text-content-primary",
  },
  // V1.6 "Impact vs current": the surface is the warning STATUS surface
  // (rgb(255,244,235)), not a warning-N shade — no raw shade matches it.
  "warning-subtle": {
    box: "bg-status-warning-surface",
    text: "text-warning-800",
  },
};

/** Props for the narrowed TonePill atom. */
export interface TonePillProps {
  /** Deep-shade pairing to render — closed enum, raw shades stay inside. */
  readonly tone: PillTone;
  /** Pill text content. */
  readonly label: string;
  /** Text style variant (bundled font+size+line-height). */
  readonly textVariant?: TextVariant;
  /** Extra layout classes (sizing/padding) from the consumer. */
  readonly className?: string;
  /** Test/a11y identity is part of the atom API (RFC standing constraint 4). */
  readonly testID?: string;
}

/**
 * Renders a rounded pill in a deep raw-shade tone pairing.
 * @param root0 - Component props
 * @param root0.tone - Deep-shade pairing to render
 * @param root0.label - Pill text content
 * @param root0.textVariant - Text style variant
 * @param root0.className - Extra layout classes from the consumer
 * @param root0.testID - Test identity forwarded to the pill container
 * @returns The tone pill element.
 */
const TonePill = ({
  tone,
  label,
  textVariant = "body",
  className,
  testID,
}: TonePillProps) => (
  <Box
    testID={testID}
    className={`items-center justify-center rounded-full ${TONE_CLASS[tone].box} ${className ?? ""}`}
  >
    <Text variant={textVariant} className={TONE_CLASS[tone].text}>
      {label}
    </Text>
  </Box>
);

export { TonePill };
